/**
 * TipTap Document Validation and Sanitization Utilities
 * 
 * This module provides utilities for validating and sanitizing TipTap document structures.
 * Extracted from useSimpleSnapshot.ts to improve separation of concerns and testability.
 * 
 * TipTap documents follow this structure:
 * {
 *   type: 'doc',
 *   content: [
 *     {
 *       type: 'paragraph',
 *       content: [
 *         { type: 'text', text: 'Hello world' }
 *       ]
 *     }
 *   ]
 * }
 */

/**
 * Validates that a document has a valid TipTap structure
 * @param doc The document to validate
 * @returns true if the document structure is valid, false otherwise
 */
export function validateTipTapDocument(doc: unknown): boolean {
  if (!doc || typeof doc !== 'object') {
    return false;
  }
  
  const docObj = doc as Record<string, unknown>;
  
  if (docObj.type !== 'doc') {
    return false;
  }
  
  if (!Array.isArray(docObj.content)) {
    return false;
  }
  
  // Validate all nodes have valid types
  for (const node of docObj.content) {
    if (!node || typeof node !== 'object') {
      return false;
    }
    
    const nodeObj = node as Record<string, unknown>;
    if (!nodeObj.type || typeof nodeObj.type !== 'string') {
      return false;
    }
    
    // Recursively validate nested content
    if (Array.isArray(nodeObj.content)) {
      for (const childNode of nodeObj.content) {
        if (!childNode || typeof childNode !== 'object') {
          return false;
        }
        const childNodeObj = childNode as Record<string, unknown>;
        if (!childNodeObj.type || typeof childNodeObj.type !== 'string') {
          return false;
        }
      }
    }
  }
  
  return true;
}

/**
 * Sanitizes a TipTap document to ensure it's valid
 * Fixes common issues like undefined types, missing content arrays, etc.
 * @param doc The document to sanitize
 * @returns A sanitized TipTap document with valid structure
 */
export function sanitizeTipTapDocument(doc: unknown): Record<string, unknown> {
  if (!doc || typeof doc !== 'object') {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  }
  
  const docObj = doc as Record<string, unknown>;
  
  if (docObj.type !== 'doc') {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  }
  
  if (!Array.isArray(docObj.content)) {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  }
  
  // Sanitize content array
  const sanitizedContent = docObj.content
    .map((node: unknown) => {
      if (!node || typeof node !== 'object') {
        return { type: 'paragraph' };
      }
      
      const nodeObj = node as Record<string, unknown>;
      if (!nodeObj.type || typeof nodeObj.type !== 'string') {
        return { type: 'paragraph' };
      }
      
      // Sanitize nested content
      if (Array.isArray(nodeObj.content)) {
        const sanitizedChildren = nodeObj.content
          .map((childNode: unknown) => {
            if (!childNode || typeof childNode !== 'object') {
              return null;
            }
            const childNodeObj = childNode as Record<string, unknown>;
            if (!childNodeObj.type || typeof childNodeObj.type !== 'string') {
              return null;
            }
            return childNode;
          })
          .filter((childNode: unknown) => childNode !== null);
        
        return {
          ...nodeObj,
          content: sanitizedChildren.length > 0 ? sanitizedChildren : undefined
        };
      }
      
      return nodeObj;
    })
    .filter((node: unknown) => node !== null);
  
  return {
    type: 'doc',
    content: sanitizedContent.length > 0 ? sanitizedContent : [{ type: 'paragraph' }]
  };
}

/**
 * Checks if a TipTap document has meaningful text content
 * An empty document has structure but no actual text nodes with content
 * @param doc The TipTap document to check
 * @returns true if the document contains meaningful text, false otherwise
 */
export function hasTipTapContent(doc: unknown): boolean {
  if (!doc || typeof doc !== 'object') {
    return false;
  }
  
  const docObj = doc as Record<string, unknown>;
  
  // TipTap doc structure: { type: 'doc', content: [...] }
  // Empty doc: { type: 'doc', content: [{ type: 'paragraph' }] }
  // Meaningful doc: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '...' }] }] }
  if (docObj.type === 'doc' && Array.isArray(docObj.content)) {
    // Check if any paragraph has text content
    const hasText = docObj.content.some((node: unknown) => {
      if (!node || typeof node !== 'object') return false;
      const nodeObj = node as Record<string, unknown>;
      if (nodeObj.type !== 'paragraph') return false;
      if (!Array.isArray(nodeObj.content)) return false;
      return nodeObj.content.some((textNode: unknown) => {
        if (!textNode || typeof textNode !== 'object') return false;
        const textNodeObj = textNode as Record<string, unknown>;
        return (
          textNodeObj.type === 'text' &&
          textNodeObj.text &&
          String(textNodeObj.text).trim().length > 0
        );
      });
    });
    
    return hasText;
  }
  
  return false;
}

